import { useState, useMemo, useRef } from "react";
import { CREDIT_META, PACKS, PLANS, ALL_ROLES_FLAT } from "./data";
import CreditBadge from "./CreditBadge";
import styles from "./pricing.module.css";
import { fmtCurrency } from "../../../../../utils/functions";

export default function CalculatorTab({ currency }) {
  const isNGN = currency === "NGN";
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);

  const dropdownResults = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) return ALL_ROLES_FLAT.slice(0, 8);
    return ALL_ROLES_FLAT.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.subcategory.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q),
    ).slice(0, 10);
  }, [searchTerm]);

  const addRole = (role) => {
    setSelectedRoles((prev) => {
      const ex = prev.find((r) => r.title === role.title);
      if (ex)
        return prev.map((r) =>
          r.title === role.title ? { ...r, qty: r.qty + 1 } : r,
        );
      return [...prev, { ...role, qty: 1 }];
    });
    setSearchTerm("");
    setShowDropdown(false);
  };

  const updateQty = (title, qty) => {
    if (qty < 1) setSelectedRoles((p) => p.filter((r) => r.title !== title));
    else
      setSelectedRoles((p) =>
        p.map((r) => (r.title === title ? { ...r, qty } : r)),
      );
  };

  const totalCredits = useMemo(
    () => selectedRoles.reduce((s, r) => s + r.credits * r.qty, 0),
    [selectedRoles],
  );

  const packRec = useMemo(() => {
    if (!totalCredits) return null;
    return (
      PACKS.find((p) => p.credits >= totalCredits) || PACKS[PACKS.length - 1]
    );
  }, [totalCredits]);

  const annualRec = useMemo(() => {
    if (!totalCredits) return null;
    const match = PLANS.find(
      (p) => p.creditsPerMonth !== null && p.creditsPerMonth >= totalCredits,
    );
    if (match)
      return { plan: match, surplus: match.creditsPerMonth - totalCredits };
    const scale = PLANS.find((p) => p.id === "scale");
    const overage = totalCredits - scale.creditsPerMonth;
    if (overage > 50)
      return { plan: PLANS.find((p) => p.id === "enterprise"), surplus: 0 };
    return { plan: scale, surplus: 0, topUp: overage };
  }, [totalCredits]);

  const breakEven = useMemo(() => {
    if (!packRec || !annualRec?.plan) return null;
    const planP = annualRec.plan[isNGN ? "ngn" : "usd"];
    const packP = packRec[isNGN ? "ngn" : "usd"];
    if (!planP || !packP) return null;
    return Math.ceil(planP / packP);
  }, [packRec, annualRec, isNGN]);

  const SummaryPanel = () => (
    <div className={styles.calcSummaryStack}>
      {/* Total */}
      <div className={styles.calcTotalCard}>
        <div className={styles.calcTotalLabel}>Credits needed</div>
        <div className={styles.calcTotalValue}>{totalCredits || "—"}</div>
        {selectedRoles.length > 0 && (
          <div className={styles.calcTotalBreakdown}>
            {selectedRoles.map((role) => (
              <div key={role.title} className={styles.calcTotalRow}>
                <span className={styles.calcTotalRoleName}>
                  {role.qty > 1 ? `${role.qty}× ` : ""}
                  {role.title}
                </span>
                <span className={styles.calcTotalRoleCredits}>
                  {role.credits * role.qty}C
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pack recommendation */}
      {totalCredits > 0 && packRec && (
        <div className={`${styles.calcRecCard} ${styles.calcRecPack}`}>
          <div className={styles.calcRecLabel}>🪙 For this hiring round</div>
          <div className={styles.calcRecName}>{packRec.name} Pack</div>
          <div className={styles.calcRecPrice}>
            {fmtCurrency(isNGN ? packRec.ngn : packRec.usd, currency)}
            <span className={styles.calcRecPriceUnit}> one-time</span>
          </div>
          <div className={styles.calcRecSub}>
            {packRec.credits}C included · {packRec.credits - totalCredits}C left
            over
          </div>
          <button className={`${styles.calcRecBtn} ${styles.calcRecBtnPack}`}>
            Buy {packRec.name} Pack →
          </button>
        </div>
      )}

      {/* Annual recommendation */}
      {totalCredits > 0 &&
        annualRec?.plan &&
        (isNGN ? annualRec.plan.ngn : annualRec.plan.usd) && (
          <div className={`${styles.calcRecCard} ${styles.calcRecAnnual}`}>
            <div className={styles.calcRecLabel}>
              📆 If you hire this often regularly
            </div>
            <div className={styles.calcRecName}>
              {annualRec.plan.name} Annual
            </div>
            <div className={styles.calcRecPrice}>
              {fmtCurrency(
                isNGN ? annualRec.plan.ngn : annualRec.plan.usd,
                currency,
              )}
              <span className={styles.calcRecPriceUnit}>/yr</span>
            </div>
            <div
              className={`${styles.calcRecSub} ${breakEven ? styles.calcRecSubTight : ""}`}
            >
              {annualRec.plan.yearCredits}C upfront ·{" "}
              {fmtCurrency(
                isNGN ? annualRec.plan.ngnPerC : annualRec.plan.usdPerC,
                currency,
              )}
              /credit effective
            </div>
            {breakEven && (
              <div className={styles.calcBreakEvenNote}>
                Pays off vs packs in {breakEven} months of hiring at this level
              </div>
            )}
            <button
              className={`${styles.calcRecBtn} ${styles.calcRecBtnAnnual}`}
            >
              See {annualRec.plan.name} Annual →
            </button>
          </div>
        )}

      {!totalCredits && (
        <div className={styles.calcEmptyTotal}>
          Add roles to compare
          <br />
          pack vs annual costs
        </div>
      )}

      {/* Notes */}
      <div className={styles.calcNotesCard}>
        <div className={styles.calcNotesList}>
          {[
            {
              dotClass: styles.calcNoteDotGreen,
              t: "Unused credits roll over — never lost",
            },
            {
              dotClass: styles.calcNoteDotPurple,
              t: "Plans valid 1 year from purchase",
            },
            {
              dotClass: styles.calcNoteDotGreen,
              t: "30-day rematch at 50% credit cost",
            },
          ].map((p, i) => (
            <div key={i} className={styles.calcNoteRow}>
              <span className={`${styles.calcNoteDot} ${p.dotClass}`} />
              <span className={styles.calcNoteText}>{p.t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <h3 className={styles.calcTitle}>Build your hiring round</h3>
      <p className={styles.calcIntro}>
        Add the roles you need to hire. We'll show you the right credit pack for
        this round and the best annual plan if you hire regularly.
      </p>

      <div className={styles.calcLayout}>
        <div>
          {/* Search */}
          <div className={styles.calcSearchWrap}>
            <input
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 160)}
              placeholder="Search roles to add..."
              className={styles.calcSearchInput}
            />
            {showDropdown && dropdownResults.length > 0 && (
              <div className={styles.calcDropdown}>
                {dropdownResults.map((role) => (
                  <div
                    key={role.title}
                    onMouseDown={() => addRole(role)}
                    className={styles.calcDropdownItem}
                  >
                    <div>
                      <span className={styles.calcDropdownItemTitle}>
                        {role.icon} {role.title}
                      </span>
                      <div className={styles.calcDropdownItemSub}>
                        {role.subcategory}
                      </div>
                    </div>
                    <CreditBadge credits={role.credits} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected roles */}
          {selectedRoles.length === 0 ? (
            <div className={styles.calcEmptyRoles}>
              Search and add roles above to get started
            </div>
          ) : (
            <div className={styles.calcSelectedList}>
              {selectedRoles.map((role) => (
                <div key={role.title} className={styles.calcSelectedRow}>
                  <div className={styles.calcSelectedInfo}>
                    <div className={styles.calcSelectedTitle}>
                      {role.icon} {role.title}
                    </div>
                    <div className={styles.calcSelectedMeta}>
                      {CREDIT_META[role.credits].label} · {role.credits}C each
                    </div>
                  </div>
                  <div className={styles.calcSelectedActions}>
                    <div className={styles.calcQtyGroup}>
                      <button
                        onClick={() => updateQty(role.title, role.qty - 1)}
                        className={styles.calcQtyBtn}
                      >
                        −
                      </button>
                      <span className={styles.calcQtyValue}>{role.qty}</span>
                      <button
                        onClick={() => updateQty(role.title, role.qty + 1)}
                        className={styles.calcQtyBtn}
                      >
                        +
                      </button>
                    </div>
                    <CreditBadge credits={role.credits * role.qty} />
                    <button
                      onClick={() => updateQty(role.title, 0)}
                      className={styles.calcRemoveBtn}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => setSelectedRoles([])}
                className={styles.calcClearBtn}
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Summary panel — sticky on desktop/tablet via CSS, inline on mobile */}
        <div className={styles.calcSummaryWrap}>
          <SummaryPanel />
        </div>
      </div>
    </div>
  );
}
